import React, { useEffect, useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { Box, Button, Stack, Chip, Divider, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useChartLayout } from "./useChartLayout";
import { PROGRAM_COLORS, PROGRAM_SUMMARY_CARD_IDS } from "./programLensConstants";
import ChartCardWrapper from "./ChartCardWrapper";
import { searchICD10Codes } from "../../data/icd10Codes";
import { searchCPTCodes } from "../../data/cptCodes";
import { searchLoincCodes } from "../../data/loincCodes";
import { searchMedications } from "../../data/medicationList";

// Components
import PatientOverviewBanner from "./components/PatientOverviewBanner";
import CareTasksCard from "./components/CareTasksCard";
import ProblemsCard from "./components/ProblemsCard";
import MedicationsCard from "./components/MedicationsCard";
import VitalsCard from "./components/VitalsCard";
import LabsCard from "./components/LabsCard";
import SocialHistoryCard from "./components/SocialHistoryCard";
import AssessmentsCard from "./components/AssessmentsCard";
import BarriersCard from "./components/BarriersCard";
import ResourcesCard from "./components/ResourcesCard";
import ProceduresCard from "./components/ProceduresCard";
import PreventiveCareCard from "./components/PreventiveCareCard";
import PreCallSummaryCard from "./components/PreCallSummaryCard";
import EncountersCard from "./components/EncountersCard";
import QualityAssuranceCard from "./components/QualityAssuranceCard";
import SocialDeterminantsCard from "./components/SocialDeterminantsCard";
import GoalsCard from "./components/GoalsCard";
import NotesCard from "./components/NotesCard";
import AllergiesCard from "./components/AllergiesCard";
import AppointmentsCard from "./components/AppointmentsCard";
import BillingRecordsCard from "./components/BillingRecordsCard";
import ImmunizationsCard from "./components/ImmunizationsCard";
import DeviceOrdersCard from "./components/DeviceOrdersCard";
import ProgramEnrollmentCard from "./components/ProgramEnrollmentCard";
import DemographicsInsuranceCard from "./components/DemographicsInsuranceCard";
import CarePlansCard from "./components/CarePlansCard";
import RpmDeviceOrderingCard from "./components/RpmDeviceOrderingCard";
import RpmDeviceReadingsCard from "./components/RpmDeviceReadingsCard";
import CcmSummaryCard from "./components/CcmSummaryCard";
import RpmSummaryCard from "./components/RpmSummaryCard";
import { changeReadingType, nowLocalInput, rpmReadingFields, rpmReadingPayloads } from "./rpmReadingFields";
import BhiSummaryCard from "./components/BhiSummaryCard";
import DeviceOrderDialog from "./components/DeviceOrderDialog";
import GenericFormDialog from "./components/GenericFormDialog";
import { DEMO_AUTHOR, DEMO_UI_ISSUES, newFormData } from "../../config/demoUiIssues";
import CarePlanDialog from "./components/CarePlanDialog";
import LookupDialog from "./components/LookupDialog";
import PreventiveCareCompletionDialog from "./components/PreventiveCareCompletionDialog";
import AssessmentDialog from "./components/AssessmentDialog";
import EncounterDrawer from "../encounters/EncounterDrawer";

import assessmentTemplates from "../../data/assessmentTemplates";

const DEFAULT_PREVENTIVE_COMPLETION = {
  completion_date: new Date().toISOString().split("T")[0],
  notes: "",
  status: "completed",
};

function formatCallTime(totalSeconds) {
  return `${Math.floor(totalSeconds / 60)}:${(totalSeconds % 60).toString().padStart(2, "0")}`;
}

function PatientChartContainer({
  patient,
  patients,
  apiBase,
  onShowSnackbar,
}) {
  const [patientSummary, setPatientSummary] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [qualityAssuranceData, setQualityAssuranceData] = useState(null);
  const [socialDeterminantsData, setSocialDeterminantsData] = useState(null);
  const [preCallData, setPreCallData] = useState(null);
  const [preCallLoading, setPreCallLoading] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [nkdaChecked, setNkdaChecked] = useState(false);

  // Form dialog state
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formType, setFormType] = useState("");
  const [formData, setFormData] = useState({});

  // Care plan dialog
  const [carePlanDialogOpen, setCarePlanDialogOpen] = useState(false);
  const [carePlanFormData, setCarePlanFormData] = useState({
    patient_id: null,
    title: "",
    description: "",
    start_date: new Date().toISOString().split("T")[0],
    goals: "",
    interventions: "",
    created_by: "",
  });

  // Device dialog
  const [deviceDialogOpen, setDeviceDialogOpen] = useState(false);
  const [deviceFormData, setDeviceFormData] = useState({
    device_type: "",
    device_name: "",
    notes: "",
  });

  // Lookup dialogs
  const [lookupState, setLookupState] = useState({
    icd: { open: false, query: "", results: [] },
    cpt: { open: false, query: "", results: [] },
    lab: { open: false, query: "", results: [] },
    med: { open: false, query: "", results: [] },
  });

  const [encounterDrawerOpen, setEncounterDrawerOpen] = useState(false);
  const [callElapsed, setCallElapsed] = useState(0);
  // Call time for the encounter note (minutes per program); counts while a chart is open, restarts per patient
  useEffect(() => {
    setCallElapsed(0);
    if (!patient?.id) return undefined;
    const id = setInterval(() => setCallElapsed((secs) => secs + 1), 1000);
    return () => clearInterval(id);
  }, [patient?.id]);
  const [activeEncounterSession, setActiveEncounterSession] = useState(null);
  const [encounterSessionLoading, setEncounterSessionLoading] = useState(false);

  // Activity log: tracks chart changes made during this encounter for AI note generation
  const [chartActivityLog, setChartActivityLog] = useState([]);
  const appendChartActivity = useCallback((entry) => {
    setChartActivityLog((prev) => [...prev, { text: entry, ts: new Date().toISOString() }]);
  }, []);

  // Preventive care completion
  const [preventiveDialogOpen, setPreventiveDialogOpen] = useState(false);
  const [preventiveItem, setPreventiveItem] = useState(null);
  const [preventiveFormData, setPreventiveFormData] = useState(DEFAULT_PREVENTIVE_COMPLETION);

  // Assessment dialog
  const [assessmentDialogOpen, setAssessmentDialogOpen] = useState(false);
  const [currentAssessmentType, setCurrentAssessmentType] = useState(null);
  const [assessmentResponses, setAssessmentResponses] = useState({});

  // Demographics dialog
  const [demographicsDialogOpen, setDemographicsDialogOpen] = useState(false);
  const [demographicsFormData, setDemographicsFormData] = useState({});

  const API = apiBase || "https://spectra-api.loafstack.dev";

  const selectedPatientId = patient?.id;

  const selectedPatientName = patient?.name || "";

  // Generate session ID for PRISM tracking
  const sessionId = useMemo(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, []);

  // Chart layout: drag-and-drop ordering + collapse state
  const {
    columnOrders,
    collapsed,
    toggleCollapsed,
    expandAll,
    collapseAll,
    agendaCardIds,
    handleDragOver,
    handleDragEnd,
    activeLenses,
    toggleLens,
    getLensColor,
  } = useChartLayout(patient?.id, preCallData);

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const enrolledPrograms = useMemo(() => {
    const enrollments = patientSummary?.program_enrollments || [];
    const programs = new Set();
    enrollments.forEach((e) => {
      const name = (e.program_name || "").toUpperCase();
      if (name.includes("CCM") || name.includes("CHRONIC CARE")) programs.add("CCM");
      if (name.includes("RPM") || name.includes("REMOTE")) programs.add("RPM");
      if (name.includes("BHI") || name.includes("BEHAVIORAL")) programs.add("BHI");
    });
    return programs;
  }, [patientSummary?.program_enrollments]);

  // Human-readable labels for each card ID
  const CARD_LABELS = {
    preCallSummary: "Pre-Call Summary",
    careTasks: "Care Tasks",
    goals: "Goals",
    preventiveCare: "Preventive Care",
    qualityAssurance: "Quality Assurance",
    socialDeterminants: "Social Determinants",
    problems: "Problems",
    medications: "Medications",
    allergies: "Allergies",
    procedures: "Procedures",
    encounters: "Encounters",
    notes: "Notes",
    appointments: "Appointments",
    assessments: "Assessments",
    labs: "Labs",
    vitals: "Vitals",
    socialHistory: "Social History",
    barriers: "Barriers",
    resources: "Resources",
    programEnrollment: "Program Enrollment",
    immunizations: "Immunizations",
    demographics: "Demographics & Insurance",
    carePlans: "Care Plans",
    deviceOrders: "Equipment Orders (DME)",
    rpmDeviceOrdering: "RPM Device Ordering",
    rpmDeviceReadings: "RPM Device Readings",
    billingRecords: "Billing Records",
    ccmSummary: "CCM Summary",
    rpmSummary: "RPM Summary",
    bhiSummary: "BHI Summary",
  };

  const selectedPatientProgramsOptions = useMemo(
    () => programs.map((p) => ({ value: p.id, label: p.name })),
    [programs]
  );

  const getFormFields = useCallback(
    (type, data = {}) => {
      switch (type) {
        case "Problem":
          return [
            { name: "code", label: "Problem Code", type: "text", required: true },
            { name: "name", label: "Problem Name", type: "text", required: true },
            {
              name: "status",
              label: "Status",
              type: "select",
              options: ["active", "resolved", "chronic"],
              required: true,
            },
            { name: "onset_date", label: "Onset Date", type: "date" },
          ];
        case "Medication":
          return [
            { name: "drug", label: "Medication Name", type: "text", required: true },
            { name: "dose", label: "Dosage", type: "text", required: true },
            { name: "frequency", label: "Frequency", type: "text", required: true },
            {
              name: "route",
              label: "Route",
              type: "select",
              options: ["oral", "injection", "topical", "inhaled"],
              required: true,
            },
            { name: "prescribed_by", label: "Prescribed By", type: "text", required: true },
            { name: "started_at", label: "Start Date", type: "datetime-local" },
            { name: "stopped_at", label: "End Date", type: "datetime-local" },
          ];
        case "Allergy":
          return [
            { name: "allergen", label: "Allergen", type: "text", required: true },
            {
              name: "allergen_type",
              label: "Allergen Type",
              type: "select",
              options: ["drug", "food", "environmental", "other"],
              required: true,
            },
            { name: "reaction", label: "Reaction", type: "text", required: true },
            {
              name: "severity",
              label: "Severity",
              type: "select",
              options: ["mild", "moderate", "severe"],
              required: true,
            },
            { name: "onset_date", label: "Onset Date", type: "date" },
          ];
        case "Vitals":
          return [
            { name: "systolic_bp", label: "Systolic BP", type: "number", required: true },
            { name: "diastolic_bp", label: "Diastolic BP", type: "number", required: true },
            { name: "heart_rate", label: "Heart Rate", type: "number", required: true },
            { name: "temperature", label: "Temperature (°F)", type: "number", required: true },
            { name: "weight_kg", label: "Weight (kg)", type: "number" },
            { name: "height_cm", label: "Height (cm)", type: "number" },
            { name: "recorded_by", label: "Recorded By", type: "text", required: true },
          ];
        case "Lab":
          return [
            { name: "name", label: "Test Name", type: "text", required: true },
            { name: "value", label: "Result", type: "number", required: true },
            { name: "unit", label: "Unit", type: "text" },
            { name: "ref_low", label: "Reference Low", type: "number" },
            { name: "ref_high", label: "Reference High", type: "number" },
            { name: "ordered_by", label: "Ordered By", type: "text", required: true },
            { name: "collected_at", label: "Collection Date", type: "datetime-local", required: true },
          ];
        case "Note":
          return [
            {
              name: "note_type",
              label: "Note Type",
              type: "select",
              recordChoice: "type",
              options: ["Progress Note", "Assessment", "Plan", "Other"],
              required: true,
            },
            {
              name: "author",
              label: "Author",
              type: "text",
              required: true,
              fill: { label: "Use my name", value: DEMO_AUTHOR, broken: DEMO_UI_ISSUES },
            },
            { name: "content", label: "Content", type: "textarea", required: true },
          ];
        case "Encounter":
          return [
            { name: "provider", label: "Provider", type: "text", required: true },
            {
              name: "encounter_type",
              label: "Encounter Type",
              type: "select",
              recordChoice: "type",
              options: ["Office Visit", "Follow-up", "Consultation", "Emergency", "Telehealth"],
              required: true,
            },
            { name: "encounter_date", label: "Encounter Date", type: "datetime-local", required: true },
            { name: "chief_complaint", label: "Chief Complaint", type: "textarea", required: true },
            { name: "diagnosis", label: "Diagnosis", type: "textarea" },
            { name: "treatment_plan", label: "Treatment Plan", type: "textarea" },
          ];
        case "Appointment":
          return [
            { name: "provider", label: "Provider", type: "text", required: true },
            {
              name: "appointment_type",
              label: "Type",
              type: "select",
              options: ["Office Visit", "Follow-up", "Consultation", "Emergency"],
              required: true,
            },
            {
              name: "scheduled_at",
              label: "Scheduled Date/Time",
              type: "datetime-local",
              required: true,
            },
            {
              name: "status",
              label: "Status",
              type: "select",
              options: ["scheduled", "completed", "cancelled", "no-show"],
              required: true,
            },
            { name: "notes", label: "Notes", type: "textarea" },
          ];
        case "Insurance":
          return [
            { name: "insurance_company", label: "Insurance Company", type: "text", required: true },
            { name: "policy_number", label: "Policy Number", type: "text", required: true },
            { name: "group_number", label: "Group Number", type: "text" },
            { name: "subscriber_name", label: "Subscriber Name", type: "text", required: true },
            {
              name: "subscriber_relationship",
              label: "Relationship",
              type: "select",
              options: ["self", "spouse", "child", "parent"],
              required: true,
            },
            { name: "is_primary", label: "Primary Insurance", type: "checkbox" },
          ];
        case "Family History":
          return [
            {
              name: "relationship",
              label: "Relationship",
              type: "select",
              options: ["mother", "father", "sibling", "grandparent", "aunt", "uncle", "cousin"],
              required: true,
            },
            { name: "condition", label: "Medical Condition", type: "text", required: true },
            { name: "age_at_diagnosis", label: "Age at Diagnosis", type: "number" },
            { name: "is_deceased", label: "Deceased", type: "checkbox" },
          ];
        case "Immunization":
          return [
            { name: "vaccine_name", label: "Vaccine Name", type: "text", required: true },
            { name: "cvx_code", label: "CVX Code", type: "text" },
            { name: "administration_date", label: "Date Administered", type: "date", required: true },
            { name: "expiration_date", label: "Expiration Date", type: "date" },
            { name: "lot_number", label: "Lot Number", type: "text" },
            { name: "provider", label: "Provider", type: "text" },
            {
              name: "route",
              label: "Route",
              type: "select",
              options: ["IM", "SC", "PO", "Intranasal", "Intradermal"],
            },
            { name: "site", label: "Administration Site", type: "text" },
            {
              name: "status",
              label: "Status",
              type: "select",
              options: ["completed", "pending", "declined"],
              required: true,
            },
            { name: "notes", label: "Notes", type: "textarea" },
          ];
        case "Social History":
          return [
            {
              name: "category",
              label: "Category",
              type: "select",
              options: ["occupation", "smoking", "alcohol", "drugs", "exercise", "diet"],
              required: true,
            },
            { name: "value", label: "Value/Status", type: "text", required: true },
            { name: "notes", label: "Additional Notes", type: "textarea" },
          ];
        case "Goal":
          return [
            { name: "goal_description", label: "Goal Description", type: "textarea", required: true },
            {
              name: "category",
              label: "Category",
              type: "select",
              options: ["clinical", "lifestyle", "functional", "social", "other"],
            },
            {
              name: "priority",
              label: "Priority",
              type: "select",
              options: ["high", "medium", "low"],
            },
            {
              name: "status",
              label: "Status",
              type: "select",
              options: ["not_started", "in_progress", "achieved", "discontinued"],
            },
            { name: "target_date", label: "Target Date", type: "date" },
            { name: "created_by", label: "Created By", type: "text" },
            { name: "notes", label: "Notes", type: "textarea" },
          ];
        case "Procedure":
          return [
            { name: "procedure_code", label: "Procedure Code", type: "text", required: true },
            { name: "procedure_name", label: "Procedure Name", type: "text", required: true },
            { name: "procedure_date", label: "Procedure Date", type: "date", required: true },
            { name: "provider", label: "Performing Provider", type: "text", required: true },
            { name: "location", label: "Location", type: "text" },
            {
              name: "status",
              label: "Status",
              type: "select",
              options: ["scheduled", "completed", "cancelled"],
              required: true,
            },
            { name: "notes", label: "Notes", type: "textarea" },
          ];
        case "Equipment Order":
          return [
            {
              name: "device_type",
              label: "Device Type",
              type: "select",
              options: [
                "CGM",
                "DME",
                "Insulin Pump",
                "CPAP",
                "Nebulizer",
                "Wheelchair",
                "Walker",
                "Other",
              ],
              required: true,
            },
            { name: "device_name", label: "Device Name", type: "text", required: true },
            { name: "manufacturer", label: "Manufacturer", type: "text" },
            { name: "ordered_by", label: "Ordered By", type: "text", required: true },
            {
              name: "status",
              label: "Status",
              type: "select",
              options: ["pending", "approved", "ordered", "delivered", "denied"],
              required: true,
            },
            { name: "insurance_auth_number", label: "Insurance Auth #", type: "text" },
            { name: "tracking_number", label: "Tracking Number", type: "text" },
            { name: "delivery_date", label: "Delivery Date", type: "date" },
            { name: "notes", label: "Notes", type: "textarea" },
          ];
        case "Barrier":
          return [
            {
              name: "barrier_type",
              label: "Barrier Type",
              type: "select",
              options: [
                "transportation",
                "financial",
                "language",
                "housing",
                "literacy",
                "insurance",
                "food_insecurity",
                "technology_access",
                "other",
              ],
              required: true,
            },
            { name: "description", label: "Description", type: "textarea", required: true },
            { name: "identified_date", label: "Identified Date", type: "date", required: true },
            { name: "identified_by", label: "Identified By", type: "text", required: true },
            {
              name: "status",
              label: "Status",
              type: "select",
              options: ["identified", "in_progress", "resolved", "ongoing"],
              required: true,
            },
            { name: "resolution_notes", label: "Resolution Notes", type: "textarea" },
          ];
        case "Resource":
          return [
            {
              name: "resource_type",
              label: "Resource Type",
              type: "select",
              options: [
                "referral",
                "community_service",
                "financial_aid",
                "transportation_voucher",
                "food_assistance",
                "housing_assistance",
                "interpreter_service",
                "other",
              ],
              required: true,
            },
            { name: "organization", label: "Organization", type: "text", required: true },
            { name: "contact_info", label: "Contact Info", type: "text" },
            { name: "description", label: "Description", type: "textarea", required: true },
            { name: "provided_date", label: "Date Provided", type: "date", required: true },
            { name: "provided_by", label: "Provided By", type: "text", required: true },
            {
              name: "status",
              label: "Status",
              type: "select",
              options: ["provided", "contacted", "completed", "declined"],
              required: true,
            },
            { name: "notes", label: "Notes", type: "textarea" },
          ];
        case "Program Enrollment":
          return [
            {
              name: "program_id",
              label: "Program",
              type: "select",
              recordChoice: "type",
              options: selectedPatientProgramsOptions,
              required: true,
            },
            { name: "enrolled_by", label: "Enrolled By", type: "text", required: true },
            { name: "enrollment_date", label: "Enrollment Date", type: "date", required: true },
            {
              name: "status",
              label: "Status",
              type: "select",
              options: ["enrolled", "active", "completed", "withdrawn"],
              required: true,
            },
            { name: "completion_date", label: "Completion Date", type: "date" },
            { name: "progress_notes", label: "Progress Notes", type: "textarea" },
          ];
        case "Preventive Care":
          return [
            {
              name: "item_type",
              label: "Item Type",
              type: "select",
              recordChoice: "type",
              options: ["vaccine", "screening", "wellness_visit"],
              required: true,
            },
            { name: "item_name", label: "Item Name", type: "text", required: true },
            { name: "due_date", label: "Due Date", type: "date", required: true },
            {
              name: "status",
              label: "Status",
              type: "select",
              options: ["due", "overdue", "completed", "scheduled", "not_applicable"],
              required: true,
            },
            {
              name: "priority",
              label: "Priority",
              type: "select",
              options: ["critical", "high", "medium", "low"],
              required: true,
            },
            { name: "frequency_months", label: "Frequency (months)", type: "number" },
            { name: "completed_date", label: "Completed Date", type: "date" },
            { name: "provider", label: "Provider", type: "text" },
            { name: "notes", label: "Notes", type: "textarea" },
          ];
        case "Care Plan":
          return [
            { name: "title", label: "Care Plan Title", type: "text", required: true },
            { name: "description", label: "Description", type: "textarea", required: true },
            { name: "start_date", label: "Start Date", type: "date", required: true },
            { name: "end_date", label: "End Date", type: "date" },
            {
              name: "status",
              label: "Status",
              type: "select",
              options: ["active", "completed", "cancelled", "on_hold"],
              required: true,
            },
            { name: "created_by", label: "Created By", type: "text", required: true },
            { name: "assigned_team", label: "Assigned Team (comma-separated)", type: "text" },
            { name: "goals", label: "Goals (JSON format)", type: "textarea", required: true },
            {
              name: "interventions",
              label: "Interventions (JSON format)",
              type: "textarea",
              required: true,
            },
            { name: "review_frequency_days", label: "Review Frequency (days)", type: "number" },
          ];
        case "Billing Record":
          return [
            { name: "service_date", label: "Service Date", type: "date", required: true },
            { name: "service_code", label: "CPT Code", type: "text", required: true },
            { name: "description", label: "Service Description", type: "text", required: true },
            {
              name: "diagnosis_codes",
              label: "ICD-10 Codes (comma-separated)",
              type: "text",
              required: true,
            },
            { name: "provider", label: "Provider", type: "text", required: true },
            { name: "amount", label: "Amount ($)", type: "number", required: true },
            { name: "insurance_claim_id", label: "Insurance Claim ID", type: "text" },
            {
              name: "status",
              label: "Status",
              type: "select",
              options: ["pending", "submitted", "paid", "denied", "appealed"],
              required: true,
            },
          ];
        case "Care Task":
          return [
            {
              name: "task_type",
              label: "Task Type",
              type: "select",
              recordChoice: "type",
              options: [
                "follow_up",
                "lab_order",
                "referral",
                "medication_review",
                "care_coordination",
                "documentation",
                "other",
              ],
              required: true,
            },
            { name: "task_description", label: "Task Description", type: "textarea", required: true },
            {
              name: "priority",
              label: "Priority",
              type: "select",
              options: ["critical", "high", "medium", "low"],
              required: true,
            },
            { name: "due_date", label: "Due Date", type: "date", required: true },
            { name: "assigned_to", label: "Assigned To", type: "text", required: true },
            {
              name: "status",
              label: "Status",
              type: "select",
              options: ["pending", "in_progress", "completed", "cancelled"],
              required: true,
            },
            { name: "notes", label: "Notes", type: "textarea" },
          ];
        case "RPM Device Order":
          return [
            {
              name: "device_type",
              label: "RPM Device Type",
              type: "select",
              recordChoice: "type",
              options: [
                "Blood Pressure Monitor",
                "Glucose Meter",
                "Pulse Oximeter",
                "Weight Scale",
                "Heart Rate Monitor",
                "Thermometer",
                "Spirometer",
                "Other",
              ],
              required: true,
            },
            { name: "manufacturer", label: "Manufacturer", type: "text" },
            { name: "model", label: "Model", type: "text" },
            { name: "order_date", label: "Order Date", type: "date", required: true },
            {
              name: "status",
              label: "Status",
              type: "select",
              options: ["pending", "ordered", "shipped", "delivered", "activated", "cancelled"],
              required: true,
            },
            { name: "tracking_number", label: "Tracking Number", type: "text" },
            { name: "delivery_date", label: "Delivery Date", type: "date" },
            { name: "notes", label: "Notes", type: "textarea" },
          ];
        case "RPM Reading":
          return rpmReadingFields(data.reading_type);
        default:
          return [];
      }
    },
    [selectedPatientProgramsOptions]
  );

  const endpointMap = useMemo(
    () => ({
      Problem: "problems",
      Medication: "medications",
      Allergy: "allergies",
      Vitals: "vitals",
      Lab: "labs",
      Note: "notes",
      Encounter: "encounters",
      Appointment: "appointments",
      Insurance: "insurance",
      "Family History": "family-history",
      Immunization: "immunizations",
      "Social History": "social-history",
      Procedure: "procedures",
      "Equipment Order": "device-orders",
      Barrier: "barriers",
      Resource: "resources",
      "Program Enrollment": "program-enrollments",
      "Preventive Care": "preventive-care-items",
      "Care Plan": "care-plans",
      "Billing Record": "billing-records",
      "Care Task": "care-tasks",
      "Goal": "goals",
      "RPM Device Order": "rpm-device-orders",
      "RPM Reading": "rpm-readings",
    }),
    []
  );

  const fetchPrograms = useCallback(async () => {
    try {
      const response = await fetch(`${API}/programs`);
      const data = await response.json();
      setPrograms(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load programs:", error);
      setPrograms([]);
    }
  }, [API]);

  const fetchPatientSummary = useCallback(async () => {
    if (!selectedPatientId) return;
    try {
      const summaryRes = await fetch(`${API}/patients/${selectedPatientId}/summary`);
      if (!summaryRes.ok) {
        throw new Error(`Failed to fetch patient summary: ${summaryRes.status}`);
      }
      const summary = await summaryRes.json();
      setPatientSummary(summary);
    } catch (error) {
      console.error("Failed to load patient data:", error);
      onShowSnackbar({
        open: true,
        message: "Failed to load patient data",
        severity: "error",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, selectedPatientId]);

  const fetchPatientAssessments = useCallback(async () => {
    if (!selectedPatientId) return;
    try {
      const response = await fetch(`${API}/patients/${selectedPatientId}/assessments`);
      if (response.ok) {
        const data = await response.json();
        setAssessments(data);
      }
    } catch (error) {
      console.error("Failed to load assessments:", error);
    }
  }, [API, selectedPatientId]);

  const fetchPreCallSummary = useCallback(async (refresh = false) => {
    if (!selectedPatientId) return;
    setPreCallLoading(true);
    try {
      const res = await fetch(`${API}/patients/${selectedPatientId}/agenda${refresh === true ? "?refresh=true" : ""}`);
      if (res.ok) {
        const agenda = await res.json();
        // Transform agenda into the shape PreCallSummaryCard expects
        const allItems = [
          ...(agenda.tiers?.P0 || []),
          ...(agenda.tiers?.P1 || []),
          ...(agenda.tiers?.P2 || []),
          ...(agenda.tiers?.P3 || []),
        ];
        setPreCallData({
          ...agenda,
          agenda_items: allItems.map((item) => ({
            topic: item.title,
            talking_point: item.detail,
            context: item.source_label,
            priority: item.tier === "P0" ? "high" : item.tier === "P1" ? "high" : item.tier === "P2" ? "medium" : "low",
            tier: item.tier,
            category: item.category,
            source_table: item.source_table,
            source_id: item.source_id,
            source_label: item.source_label,
          })),
          estimated_duration_min: agenda.estimated_duration_min || 15,
        });
      } else {
        console.warn('Agenda endpoint failed:', res.status);
        setPreCallData(null);
      }
    } catch (error) {
      console.warn("Failed to load pre-call agenda:", error);
      setPreCallData(null);
    } finally {
      setPreCallLoading(false);
    }
  }, [API, selectedPatientId]);

  const handleAddressPreCallItem = useCallback(async (addressedIndices) => {
    if (!selectedPatientId) return;
    try {
      // 404 means the agenda came from the rules engine and has no saved summary to update
      await fetch(`${API}/pre-call/${selectedPatientId}/address`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ addressed_indices: addressedIndices, summary_id: preCallData?.summary_id ?? null }),
      });
    } catch (error) {
      console.warn("Failed to update addressed items:", error);
    }
  }, [API, selectedPatientId, preCallData?.summary_id]);

  const handleMarkPreCallUsed = useCallback(async () => {
    if (!selectedPatientId) return;
    try {
      await fetch(`${API}/pre-call/${selectedPatientId}/use`, { method: "POST" });
    } catch (error) {
      console.warn("Failed to mark pre-call used:", error);
    }
  }, [API, selectedPatientId]);

  const fetchAIFeatures = useCallback(async () => {
    if (!selectedPatientId) return;
    try {
      const qaRes = await fetch(`${API}/patients/${selectedPatientId}/quality-assurance`);
      if (qaRes.ok) {
        const qaData = await qaRes.json();
        setQualityAssuranceData(qaData);
      } else {
        console.warn('Quality assurance endpoint failed:', qaRes.status);
        setQualityAssuranceData({ status: "unavailable", findings: [], findings_count: 0, barriers_identified: 0 });
      }
    } catch (error) {
      console.warn("Failed to load quality assurance:", error);
      setQualityAssuranceData({ status: "unavailable", findings: [], findings_count: 0, barriers_identified: 0 });
    }

    // NOTE: talking-points endpoint removed — replaced by /patients/{id}/agenda (pyramid builder)

    try {
      const sdohRes = await fetch(`${API}/patients/${selectedPatientId}/social-determinants`);
      if (sdohRes.ok) {
        const sdohData = await sdohRes.json();
        setSocialDeterminantsData(sdohData);
      } else {
        console.warn('Social determinants endpoint failed:', sdohRes.status);
        setSocialDeterminantsData({ status: "unavailable", findings: [], findings_count: 0, barriers_identified: 0 });
      }
    } catch (error) {
      console.warn("Failed to load social determinants:", error);
      setSocialDeterminantsData({ status: "unavailable", findings: [], findings_count: 0, barriers_identified: 0 });
    }
  }, [API, selectedPatientId]);

  const ensureEncounterSession = useCallback(async () => {
    if (!selectedPatientId) return null;
    setEncounterSessionLoading(true);
    try {
      const existingRes = await fetch(`${API}/patients/${selectedPatientId}/encounter-sessions?status=active`);
      if (existingRes.ok) {
        const sessions = await existingRes.json();
        if (Array.isArray(sessions) && sessions.length > 0) {
          return sessions[0];
        }
      }

      const createRes = await fetch(`${API}/encounter-sessions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          patient_id: selectedPatientId,
          created_by: "Care Coordinator",
          program_type: "CCM",
          encounter_mode: "phone",
          encounter_focus: `Monthly outreach - ${selectedPatientName}`,
        }),
      });
      if (!createRes.ok) {
        throw new Error(`Failed to create encounter session: ${createRes.status}`);
      }
      return await createRes.json();
    } catch (error) {
      console.error("Unable to initialize encounter session:", error);
      onShowSnackbar({
        open: true,
        message: "Unable to open encounter workspace",
        severity: "error",
      });
      return null;
    } finally {
      setEncounterSessionLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, selectedPatientId, selectedPatientName]);

  useEffect(() => {
    fetchPrograms();
  }, [fetchPrograms]);

  useEffect(() => {
    if (!selectedPatientId) return;
    fetchPatientSummary();
    fetchPatientAssessments();
  }, [selectedPatientId, fetchPatientSummary, fetchPatientAssessments]);

  // Separate useEffect for AI features - runs only once per patient
  useEffect(() => {
    if (!selectedPatientId) return;
    fetchAIFeatures();
    fetchPreCallSummary();
  }, [selectedPatientId]); // Only depends on selectedPatientId, not the callback itself

  useEffect(() => {
    setNkdaChecked(false);
  }, [selectedPatientId]);

  useEffect(() => {
    setEncounterDrawerOpen(false);
    setActiveEncounterSession(null);
    setEncounterSessionLoading(false);
  }, [selectedPatientId]);

  const handleAddToChart = (section) => {
    if (!selectedPatientId) {
      onShowSnackbar({
        open: true,
        message: "Please select a patient first",
        severity: "warning",
      });
      return;
    }

    setFormType(section);
    setFormData(section === "RPM Reading" ? { reading_date: nowLocalInput() } : newFormData(section));
    setFormDialogOpen(true);
  };

  const handleOpenEncounterDrawer = useCallback(async () => {
    if (!selectedPatientId) {
      onShowSnackbar({
        open: true,
        message: "Please select a patient first",
        severity: "warning",
      });
      return;
    }
    setEncounterDrawerOpen(true);
    const sessionData = await ensureEncounterSession();
    if (sessionData) {
      setActiveEncounterSession(sessionData);
    }
  }, [ensureEncounterSession, onShowSnackbar, selectedPatientId]);

  const handleEncounterDrawerClose = useCallback(() => {
    setEncounterDrawerOpen(false);
  }, []);

  const handleEncounterSessionChange = useCallback((updatedSession) => {
    setActiveEncounterSession(updatedSession);
  }, []);

  const handleEncounterComplete = useCallback(() => {
    setActiveEncounterSession(null);
    setEncounterDrawerOpen(false);
    setChartActivityLog([]);
    fetchPatientSummary();
  }, [fetchPatientSummary]);

  const handleOpenLookup = (type) => {
    setLookupState((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        open: true,
        query: "",
        results: [],
      },
    }));
  };

  const handleLookupSearch = (type, query) => {
    let results = [];
    if (query.length >= 2) {
      switch (type) {
        case "icd":
          results = searchICD10Codes(query);
          break;
        case "cpt":
          results = searchCPTCodes(query);
          break;
        case "lab":
          results = searchLoincCodes(query);
          break;
        case "med":
          results = searchMedications(query);
          break;
        default:
          break;
      }
    }

    setLookupState((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        query,
        results,
      },
    }));
  };

  const handleLookupSelect = (type, result) => {
    switch (type) {
      case "icd":
        setFormType("Problem");
        setFormData({ code: result.code, name: result.description, status: "active" });
        break;
      case "cpt":
        setFormType("Procedure");
        setFormData({
          procedure_code: result.code,
          procedure_name: result.description,
          status: "scheduled",
        });
        break;
      case "lab":
        setFormType("Lab");
        setFormData({
          name: result.description,
          loinc_code: result.code,
        });
        break;
      case "med":
        setFormType("Medication");
        setFormData({
          drug: result.description,
          rxnorm_code: result.code,
        });
        break;
      default:
        break;
    }

    setLookupState((prev) => ({
      ...prev,
      [type]: { ...prev[type], open: false },
    }));
    setFormDialogOpen(true);
  };

  const handleSubmitForm = async () => {
    if (!selectedPatientId) {
      onShowSnackbar({
        open: true,
        message: "No patient selected",
        severity: "error",
      });
      return;
    }

    const endpoint = endpointMap[formType];
    if (!endpoint) {
      onShowSnackbar({
        open: true,
        message: `Invalid form type: ${formType}`,
        severity: "error",
      });
      return;
    }

    // Validate required fields
    const fields = getFormFields(formType, formData);
    const requiredFields = fields.filter((f) => f.required);
    const missingFields = requiredFields.filter((f) => !formData[f.name] || formData[f.name] === "");

    if (missingFields.length > 0) {
      const missingFieldNames = missingFields.map((f) => f.label).join(", ");
      onShowSnackbar({
        open: true,
        message: `Required fields missing: ${missingFieldNames}`,
        severity: "error",
      });
      return;
    }

    // Check if we're editing an existing item
    const isEditing = formData._editingId;
    const editingId = formData._editingId;

    // Remove the _editingId from the payload
    const { _editingId, ...cleanFormData } = formData;

    // Blank optional fields go out as null (not "": the API rejects "" for dates and numbers, so the whole
    // item failed to save), and number fields as numbers.
    const numberFields = new Set(fields.filter((f) => f.type === "number").map((f) => f.name));
    const normalized = Object.fromEntries(Object.entries(cleanFormData).map(([key, value]) => {
      if (value === "") return [key, null];
      if (numberFields.has(key) && typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value))) return [key, Number(value)];
      return [key, value];
    }));
    const payload = {
      ...normalized,
      patient_id: selectedPatientId,
    };

    try {
      const url = isEditing ? `${API}/${endpoint}/${editingId}` : `${API}/${endpoint}`;
      const method = isEditing ? "PUT" : "POST";

      // An RPM pulse oximeter reading with a pulse is saved as two readings (SpO2 and Pulse)
      const [first, ...extra] = formType === "RPM Reading" && !isEditing ? rpmReadingPayloads(payload) : [payload];
      const response = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(first),
      });
      if (response.ok) {
        for (const body of extra) {
          await fetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
        }
      }

      if (response.ok) {
        const activityLine = `${isEditing ? 'Updated' : 'Added'} ${formType}: ${cleanFormData.description || cleanFormData.name || cleanFormData.drug || cleanFormData.title || cleanFormData.type || ''}`;
        appendChartActivity(activityLine.replace(/:\s*$/, ''));
        onShowSnackbar({
          open: true,
          message: encounterDrawerOpen
            ? `${formType} ${isEditing ? 'updated' : 'added'} ✓ — added to encounter notes`
            : `${formType} ${isEditing ? 'updated' : 'added'} successfully`,
          severity: "success",
        });
        setFormDialogOpen(false);
        await fetchPatientSummary();
      } else {
        let errorMsg = `Failed to ${isEditing ? 'update' : 'add'} ${formType}`;
        try {
          const error = await response.json();
          if (Array.isArray(error.detail)) {
            errorMsg = error.detail.map((e) => e.msg).join(", ");
          } else if (typeof error.detail === "string") {
            errorMsg = error.detail;
          } else if (error.detail && typeof error.detail === "object") {
            errorMsg = JSON.stringify(error.detail);
          }
        } catch (parseError) {
          const text = await response.text();
          if (text) errorMsg = text;
        }

        onShowSnackbar({ open: true, message: errorMsg, severity: "error" });
      }
    } catch (error) {
      onShowSnackbar({
        open: true,
        message: `Failed to ${isEditing ? 'update' : 'add'} ${formType}: ${error.message}`,
        severity: "error",
      });
    }
  };

  const analyzePatientCareGaps = async () => {
    if (!selectedPatientId) return;

    try {
      onShowSnackbar({
        open: true,
        message: "Analyzing care gaps with AI...",
        severity: "info",
      });

      const response = await fetch(`${API}/patients/${selectedPatientId}/analyze-care-gaps`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      if (response.ok) {
        const result = await response.json();
        onShowSnackbar({
          open: true,
          message: `Analysis complete: ${result.gaps_identified} gaps found, ${result.tasks_generated} tasks generated`,
          severity: "success",
        });
        await fetchPatientSummary();
      } else {
        const error = await response.json();
        const errorMsg = Array.isArray(error.detail)
          ? error.detail.map((e) => e.msg).join(", ")
          : error.detail || "Failed to analyze care gaps";
        onShowSnackbar({ open: true, message: errorMsg, severity: "error" });
      }
    } catch (error) {
      onShowSnackbar({
        open: true,
        message: `Failed to analyze: ${error.message}`,
        severity: "error",
      });
    }
  };

  const completeTask = async (taskId) => {
    try {
      const response = await fetch(`${API}/care-tasks/${taskId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          status: "completed",
          completed_date: new Date().toISOString().split("T")[0],
          completed_by: "Current User",
        }),
      });

      if (response.ok) {
        appendChartActivity('Completed care task');
        onShowSnackbar({
          open: true,
          message: encounterDrawerOpen
            ? "Task completed ✓ — added to encounter notes"
            : "Task marked as complete",
          severity: "success",
        });
        await fetchPatientSummary();
      } else {
        onShowSnackbar({
          open: true,
          message: "Failed to update task",
          severity: "error",
        });
      }
    } catch (error) {
      onShowSnackbar({
        open: true,
        message: "Failed to update task",
        severity: "error",
      });
    }
  };

  const handleDeleteChartItem = async (itemType, itemId) => {
    const endpoint = {
      problem: "problems",
      medication: "medications",
      allergy: "allergies",
      vital: "vitals",
      lab: "labs",
      note: "notes",
      appointment: "appointments",
      preventive_care: "preventive-care-items",
      social_history: "social-history",
      procedure: "procedures",
      care_task: "care-tasks",
      goal: "goals",
      device_order: "device-orders",
      enrollment: "program-enrollments",
      insurance: "insurance",
      care_plan: "care-plans",
      barrier: "barriers",
      resource: "resources",
      rpm_device_order: "rpm-device-orders",
      rpm_reading: "rpm-readings",
      immunization: "immunizations",
      billing_record: "billing-records",
    }[itemType];

    if (!endpoint) {
      onShowSnackbar({ open: true, message: "Invalid item type", severity: "error" });
      return;
    }

    try {
      const response = await fetch(`${API}/${endpoint}/${itemId}`, {
        method: "DELETE",
      });

      if (response.ok) {
        onShowSnackbar({
          open: true,
          message: `${itemType} deleted successfully`,
          severity: "success",
        });
        await fetchPatientSummary();
      } else {
        onShowSnackbar({
          open: true,
          message: `Failed to delete ${itemType}`,
          severity: "error",
        });
      }
    } catch (error) {
      onShowSnackbar({
        open: true,
        message: `Failed to delete ${itemType}`,
        severity: "error",
      });
    }
  };

  const handleToggleGoal = async (goalId) => {
    const goal = patientSummary?.goals?.find(g => g.id === goalId);
    if (!goal) return;

    const newStatus = goal.status === "achieved" ? "in_progress" : "achieved";

    try {
      const response = await fetch(`${API}/goals/${goalId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          status: newStatus,
          achieved_date: newStatus === "achieved" ? new Date().toISOString().split("T")[0] : null,
        }),
      });

      if (response.ok) {
        appendChartActivity(`Goal ${newStatus === "achieved" ? "achieved" : "reopened"}: ${goal.goal_description || "goal"}`);
        onShowSnackbar({
          open: true,
          message: encounterDrawerOpen
            ? `Goal ${newStatus === "achieved" ? "marked as achieved" : "unmarked"} ✓ — added to encounter notes`
            : `Goal ${newStatus === "achieved" ? "marked as achieved" : "unmarked"}`,
          severity: "success",
        });
        await fetchPatientSummary();
      } else {
        onShowSnackbar({
          open: true,
          message: "Failed to update goal",
          severity: "error",
        });
      }
    } catch (error) {
      onShowSnackbar({
        open: true,
        message: "Failed to update goal",
        severity: "error",
      });
    }
  };

  const handleEditGoal = (goal) => {
    // Pre-populate form data with existing goal data
    setFormType("Goal");
    setFormData({
      goal_description: goal.goal_description,
      category: goal.category,
      priority: goal.priority,
      status: goal.status,
      target_date: goal.target_date,
      notes: goal.notes,
      _editingId: goal.id, // Store the ID for the update
    });
    setFormDialogOpen(true);
  };

  const handleDeviceOrderSubmit = async () => {
    if (!selectedPatientId) {
      onShowSnackbar({
        open: true,
        message: "No patient selected",
        severity: "error",
      });
      return;
    }
    if (!deviceFormData.device_type) {
      onShowSnackbar({
        open: true,
        message: "Please select a device type",
        severity: "error",
      });
      return;
    }

    try {
      // Remote-monitoring devices are RPM orders: they belong with the RPM Device Orders card and RPM summary
      const RPM_DEVICE_TYPES = {
        bp_monitor: "Blood Pressure Monitor", glucose_meter: "Glucose Meter", pulse_ox: "Pulse Oximeter",
        weight_scale: "Weight Scale", thermometer: "Thermometer", ecg_monitor: "Heart Rate Monitor",
      };
      const response = await fetch(`${API}/rpm-device-orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          patient_id: selectedPatientId,
          device_type: RPM_DEVICE_TYPES[deviceFormData.device_type] || deviceFormData.device_type,
          model: deviceFormData.device_name || null,
          order_date: new Date().toISOString().slice(0, 10),
          status: "pending",
          notes: deviceFormData.notes,
        }),
      });

      if (response.ok) {
        appendChartActivity(`Ordered device: ${deviceFormData.device_type || deviceFormData.device_name || "device"}`);
        onShowSnackbar({
          open: true,
          message: encounterDrawerOpen
            ? "Device order submitted ✓ — added to encounter notes"
            : "Device order submitted successfully",
          severity: "success",
        });
        setDeviceDialogOpen(false);
        setDeviceFormData({ device_type: "", device_name: "", notes: "" });
        await fetchPatientSummary();
      } else {
        const error = await response.json();
        const errorMsg = Array.isArray(error.detail)
          ? error.detail.map((e) => e.msg).join(", ")
          : error.detail || "Failed to submit device order";
        onShowSnackbar({ open: true, message: errorMsg, severity: "error" });
      }
    } catch (error) {
      onShowSnackbar({
        open: true,
        message: "Failed to submit device order",
        severity: "error",
      });
    }
  };

  const handlePreventiveCareComplete = (item) => {
    setPreventiveItem(item);
    setPreventiveFormData({
      ...DEFAULT_PREVENTIVE_COMPLETION,
      status: "completed",
    });
    setPreventiveDialogOpen(true);
  };

  const handleSavePreventiveCareCompletion = async () => {
    if (!preventiveItem) return;

    try {
      const response = await fetch(`${API}/preventive-care-items/${preventiveItem.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          status: preventiveFormData.status,
          completion_date: preventiveFormData.completion_date,
          notes: preventiveFormData.notes,
        }),
      });

      if (response.ok) {
        appendChartActivity(`Updated preventive care: ${preventiveItem.name || preventiveItem.description || 'item'} → ${preventiveFormData.status}`);
        onShowSnackbar({
          open: true,
          message: encounterDrawerOpen
            ? "Preventive care updated ✓ — added to encounter notes"
            : "Preventive care item updated",
          severity: "success",
        });
        setPreventiveDialogOpen(false);
        await fetchPatientSummary();
      } else {
        onShowSnackbar({
          open: true,
          message: "Failed to update item",
          severity: "error",
        });
      }
    } catch (error) {
      onShowSnackbar({
        open: true,
        message: "Failed to update item",
        severity: "error",
      });
    }
  };

  const handleStartAssessment = (type) => {
    setCurrentAssessmentType(type);
    setAssessmentResponses({});
    setAssessmentDialogOpen(true);
  };

  const handleAssessmentResponseChange = (questionId, value, score) => {
    setAssessmentResponses((prev) => ({
      ...prev,
      [questionId]: { value, score },
    }));
  };

  const getRecommendations = (assessmentType, riskLevel) => {
    const recommendations = {
      sdoh: {
        low: "Continue regular monitoring.",
        medium: "Consider community resource referrals. Schedule follow-up in 3 months.",
        high: "Immediate social work consultation. Connect with community resources. Schedule follow-up in 1 month.",
      },
      fall_risk: {
        low: "Continue regular safety practices. Annual reassessment recommended.",
        medium: "Home safety evaluation recommended. Consider physical therapy referral. Medication review.",
        high: "Immediate fall prevention interventions. PT/OT referral. Home safety assessment. Consider assistive devices.",
      },
      home_safety: {
        low: "Home environment is safe. Annual reassessment recommended.",
        medium: "Minor safety improvements recommended. Provide safety checklist.",
        high: "Urgent home modifications needed. Consider home health referral. Provide safety equipment resources.",
      },
      nutrition: {
        low: "Continue healthy eating habits. Annual screening recommended.",
        medium: "Dietary counseling recommended. Consider nutrition education materials.",
        high: "Dietitian referral recommended. Assess for community food resources. Consider meal delivery programs.",
      },
      phq9: {
        low: "Minimal depression. Continue monitoring.",
        medium: "Consider counseling or watchful waiting. Reassess in 2-4 weeks.",
        high: "Active treatment recommended. Consider psychiatry referral. Safety assessment if suicidal ideation present.",
        critical: "Immediate psychiatric evaluation. Safety assessment required. Consider medication and therapy.",
      },
      gad7: {
        low: "Minimal anxiety. Continue monitoring. Reassess if symptoms emerge.",
        medium: "Mild anxiety. Consider psychoeducation and self-management strategies. Reassess in 4 weeks.",
        high: "Moderate anxiety. Consider CBT referral or pharmacotherapy. Reassess in 2 weeks.",
        critical: "Severe anxiety. Urgent behavioral health referral. Consider medication. Safety assessment.",
      },
      adl: {
        low: "Highly independent. Encourage continued activity. Annual reassessment.",
        medium: "Mild functional limitations. Consider PT/OT evaluation. Explore community support services.",
        high: "Moderate dependence. Home health aide recommended. Caregiver education. PT/OT referral.",
        critical: "Severe dependence. Comprehensive care needs assessment. Consider SNF or assisted living evaluation.",
      },
      hra: {
        low: "Low overall health risk. Continue preventive care and annual wellness visit.",
        medium: "Moderate risk factors identified. Schedule targeted follow-ups. Address modifiable risks.",
        high: "High health risk. Intensive care coordination. Address social determinants and clinical gaps.",
        critical: "Very high risk. Immediate multidisciplinary intervention. Weekly check-ins recommended.",
      },
    };

    return recommendations[assessmentType]?.[riskLevel] || "Follow up as clinically indicated.";
  };

  const handleSubmitAssessment = async () => {
    if (!selectedPatientId || !currentAssessmentType) return;

    const template = assessmentTemplates[currentAssessmentType];
    if (!template) return;

    let totalScore = 0;
    Object.values(assessmentResponses).forEach((response) => {
      totalScore += response.score || 0;
    });

    const interpretation = template.interpretation(totalScore);

    const assessmentPayload = {
      patient_id: selectedPatientId,
      assessment_type: currentAssessmentType,
      assessment_name: template.name,
      completed_by: "Current User",
      responses: JSON.stringify(assessmentResponses),
      total_score: totalScore,
      max_score: template.maxScore,
      risk_level: interpretation.level,
      interpretation: interpretation.text,
      recommendations: getRecommendations(currentAssessmentType, interpretation.level),
    };

    try {
      const response = await fetch(`${API}/assessments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(assessmentPayload),
      });

      if (response.ok) {
        const activityLine = `Completed ${template.name} — score ${totalScore}/${template.maxScore}, ${interpretation.level} risk`;
        appendChartActivity(activityLine);
        onShowSnackbar({
          open: true,
          message: encounterDrawerOpen
            ? `Assessment completed ✓ — added to encounter notes`
            : "Assessment completed successfully",
          severity: "success",
        });
        setAssessmentDialogOpen(false);
        setCurrentAssessmentType(null);
        setAssessmentResponses({});
        await fetchPatientAssessments();
      } else {
        const error = await response.json();
        const errorMsg = Array.isArray(error.detail)
          ? error.detail.map((e) => e.msg).join(", ")
          : error.detail || "Failed to submit assessment";
        onShowSnackbar({ open: true, message: errorMsg, severity: "error" });
      }
    } catch (error) {
      onShowSnackbar({
        open: true,
        message: "Failed to submit assessment",
        severity: "error",
      });
    }
  };

  const handleCreateCarePlan = () => {
    setCarePlanFormData((prev) => ({
      ...prev,
      patient_id: selectedPatientId || prev.patient_id,
      title: "",
      description: "",
      start_date: new Date().toISOString().split("T")[0],
      goals: "",
      interventions: "",
      created_by: "",
    }));
    setCarePlanDialogOpen(true);
  };

  const handleEditDemographics = () => {
    if (!patient) return;
    setDemographicsFormData({
      phone: patient.phone || "",
      email: patient.email || "",
      address: patient.address || "",
      dob: patient.dob || "",
      sex: patient.sex || "",
    });
    setDemographicsDialogOpen(true);
  };

  const handleSaveDemographics = async () => {
    if (!selectedPatientId) {
      onShowSnackbar({
        open: true,
        message: "No patient selected",
        severity: "error",
      });
      return;
    }

    try {
      const response = await fetch(`${API}/patients/${selectedPatientId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(demographicsFormData),
      });

      if (response.ok) {
        onShowSnackbar({
          open: true,
          message: "Demographics updated successfully",
          severity: "success",
        });
        setDemographicsDialogOpen(false);
        await fetchPatientSummary();
      } else {
        const error = await response.json();
        const errorMsg = Array.isArray(error.detail)
          ? error.detail.map((e) => e.msg).join(", ")
          : error.detail || "Failed to update demographics";
        onShowSnackbar({ open: true, message: errorMsg, severity: "error" });
      }
    } catch (error) {
      onShowSnackbar({
        open: true,
        message: "Failed to update demographics",
        severity: "error",
      });
    }
  };

  const handleSaveCarePlan = async () => {
    let patientId = carePlanFormData.patient_id;
    if (selectedPatientId) {
      patientId = selectedPatientId;
    }
    if (!patientId) {
      onShowSnackbar({
        open: true,
        message: "Please select a patient",
        severity: "error",
      });
      return;
    }
    try {
      const response = await fetch(`${API}/care-plans`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          patient_id: patientId,
          title: carePlanFormData.title,
          description: carePlanFormData.description,
          start_date: carePlanFormData.start_date,
          status: "active",
          created_by: carePlanFormData.created_by,
          goals: carePlanFormData.goals,
          interventions: carePlanFormData.interventions,
        }),
      });
      if (response.ok) {
        appendChartActivity(`Created care plan: ${carePlanFormData.title || 'New care plan'}`);
        onShowSnackbar({
          open: true,
          message: encounterDrawerOpen
            ? "Care plan created ✓ — added to encounter notes"
            : "Care plan created successfully",
          severity: "success",
        });
        setCarePlanDialogOpen(false);
        await fetchPatientSummary();
      } else {
        const error = await response.json();
        const errorMsg = Array.isArray(error.detail)
          ? error.detail.map((e) => e.msg).join(", ")
          : error.detail || "Failed to create care plan";
        onShowSnackbar({ open: true, message: errorMsg, severity: "error" });
      }
    } catch (error) {
      onShowSnackbar({
        open: true,
        message: "Failed to create care plan",
        severity: "error",
      });
    }
  };

  if (!patient) {
    return null;
  }

  // Maps card ID → rendered JSX element
  const getCardElement = (id) => {
    const map = {
      preCallSummary: (
        <PreCallSummaryCard
          preCallData={preCallData}
          loading={preCallLoading}
          onRefresh={() => fetchPreCallSummary(true)}
          onMarkUsed={handleMarkPreCallUsed}
          onAddressItem={handleAddressPreCallItem}
        />
      ),
      careTasks: (
        <CareTasksCard
          tasks={patientSummary?.care_tasks}
          onAddTask={() => handleAddToChart("Care Task")}
          onAnalyzeCareGaps={analyzePatientCareGaps}
          analyzeDisabled={!patient}
          onToggleComplete={completeTask}
          onEditTask={() =>
            onShowSnackbar({ open: true, message: "Edit task feature coming soon", severity: "info" })
          }
          onDeleteTask={(taskId) => handleDeleteChartItem("care_task", taskId)}
        />
      ),
      goals: (
        <GoalsCard
          goals={patientSummary?.goals}
          onAddGoal={() => handleAddToChart("Goal")}
          onDeleteGoal={(id) => handleDeleteChartItem("goal", id)}
          onToggleGoal={handleToggleGoal}
          onEditGoal={handleEditGoal}
        />
      ),
      preventiveCare: (
        <PreventiveCareCard
          items={patientSummary?.preventive_care_items || []}
          onAddItem={() => handleAddToChart("Preventive Care")}
          onMarkComplete={handlePreventiveCareComplete}
          onDeleteItem={(id) => handleDeleteChartItem("preventive_care", id)}
        />
      ),
      qualityAssurance: <QualityAssuranceCard qaData={qualityAssuranceData} />,
      socialDeterminants: <SocialDeterminantsCard socialData={socialDeterminantsData} />,
      problems: (
        <ProblemsCard
          problems={patientSummary?.problems}
          onAddProblem={() => handleAddToChart("Problem")}
          onOpenLookup={() => handleOpenLookup("icd")}
          onDeleteProblem={(id) => handleDeleteChartItem("problem", id)}
        />
      ),
      medications: (
        <MedicationsCard
          medications={patientSummary?.medications}
          onAddMedication={() => handleAddToChart("Medication")}
          onOpenLookup={() => handleOpenLookup("med")}
          onDeleteMedication={(id) => handleDeleteChartItem("medication", id)}
        />
      ),
      allergies: (
        <AllergiesCard
          allergies={patientSummary?.allergies}
          hasNKDA={nkdaChecked}
          onAddAllergy={() => handleAddToChart("Allergy")}
          onDeleteAllergy={(id) => handleDeleteChartItem("allergy", id)}
        />
      ),
      procedures: (
        <ProceduresCard
          procedures={patientSummary?.procedures}
          onAddProcedure={() => handleAddToChart("Procedure")}
          onOpenLookup={() => handleOpenLookup("cpt")}
          onDeleteProcedure={(id) => handleDeleteChartItem("procedure", id)}
        />
      ),
      encounters: (
        <EncountersCard
          encounters={patientSummary?.encounters}
          activeSessions={patientSummary?.active_encounter_sessions}
          onOpenSession={handleOpenEncounterDrawer}
          onAddEncounter={() => handleAddToChart("Encounter")}
        />
      ),
      notes: (
        <NotesCard
          notes={patientSummary?.notes}
          onAddNote={() => handleAddToChart("Note")}
          onDeleteNote={(id) => handleDeleteChartItem("note", id)}
        />
      ),
      appointments: (
        <AppointmentsCard
          appointments={patientSummary?.appointments}
          onAddAppointment={() => handleAddToChart("Appointment")}
          onDeleteAppointment={(id) => handleDeleteChartItem("appointment", id)}
        />
      ),
      assessments: (
        <AssessmentsCard
          assessments={assessments}
          onStartAssessment={handleStartAssessment}
        />
      ),
      labs: (
        <LabsCard
          labs={patientSummary?.labs}
          onAddLab={() => handleAddToChart("Lab")}
          onOpenLookup={() => handleOpenLookup("lab")}
          onDeleteLab={(id) => handleDeleteChartItem("lab", id)}
        />
      ),
      vitals: (
        <VitalsCard
          vitals={patientSummary?.vitals}
          onAddVitals={() => handleAddToChart("Vitals")}
          onDeleteVital={(id) => handleDeleteChartItem("vital", id)}
        />
      ),
      socialHistory: (
        <SocialHistoryCard
          history={patientSummary?.social_history}
          onAddSocialHistory={() => handleAddToChart("Social History")}
          onDeleteSocialHistory={(id) => handleDeleteChartItem("social_history", id)}
        />
      ),
      barriers: (
        <BarriersCard
          barriers={patientSummary?.barriers}
          onAddBarrier={() => handleAddToChart("Barrier")}
          onDeleteBarrier={(id) => handleDeleteChartItem("barrier", id)}
        />
      ),
      resources: (
        <ResourcesCard
          resources={patientSummary?.resources}
          onAddResource={() => handleAddToChart("Resource")}
          onDeleteResource={(id) => handleDeleteChartItem("resource", id)}
        />
      ),
      programEnrollment: (
        <ProgramEnrollmentCard
          enrollments={patientSummary?.program_enrollments}
          onAddEnrollment={() => handleAddToChart("Program Enrollment")}
          onDeleteEnrollment={(id) => handleDeleteChartItem("enrollment", id)}
        />
      ),
      immunizations: (
        <ImmunizationsCard
          immunizations={patientSummary?.immunizations}
          onAddImmunization={() => handleAddToChart("Immunization")}
          onDeleteImmunization={(id) => handleDeleteChartItem("immunization", id)}
        />
      ),
      demographics: (
        <DemographicsInsuranceCard
          patient={patient}
          insurance={patientSummary?.insurance}
          onAddInsurance={() => handleAddToChart("Insurance")}
          onDeleteInsurance={(id) => handleDeleteChartItem("insurance", id)}
          onEditDemographics={handleEditDemographics}
        />
      ),
      carePlans: (
        <CarePlansCard
          carePlans={patientSummary?.care_plans}
          onAddCarePlan={handleCreateCarePlan}
          onDeleteCarePlan={(id) => handleDeleteChartItem("care_plan", id)}
        />
      ),
      deviceOrders: (
        <DeviceOrdersCard
          deviceOrders={patientSummary?.device_orders}
          onAddDeviceOrder={() => handleAddToChart("Equipment Order")}
          onDeleteDeviceOrder={(id) => handleDeleteChartItem("device_order", id)}
        />
      ),
      rpmDeviceOrdering: (
        <RpmDeviceOrderingCard
          rpmDeviceOrders={patientSummary?.rpm_device_orders}
          onAddRpmDeviceOrder={() => handleAddToChart("RPM Device Order")}
          onDeleteRpmDeviceOrder={(id) => handleDeleteChartItem("rpm_device_order", id)}
        />
      ),
      rpmDeviceReadings: (
        <RpmDeviceReadingsCard
          rpmReadings={patientSummary?.rpm_readings}
          onAddRpmReading={() => handleAddToChart("RPM Reading")}
          onDeleteRpmReading={(id) => handleDeleteChartItem("rpm_reading", id)}
        />
      ),
      billingRecords: (
        <BillingRecordsCard
          billingRecords={patientSummary?.billing_records}
          onAddBillingRecord={() => handleAddToChart("Billing Record")}
          onDeleteBillingRecord={(id) => handleDeleteChartItem("billing_record", id)}
          apiBase={API}
          patientId={selectedPatientId}
          onBillingAdded={fetchPatientSummary}
        />
      ),
      ccmSummary: (
        <CcmSummaryCard
          patientId={selectedPatientId}
          patientSummary={patientSummary}
          apiBase={API}
          onShowSnackbar={onShowSnackbar}
          onAddGoal={() => handleAddToChart("Goal")}
          onEditGoal={(g) => onShowSnackbar({ open: true, message: `Edit goal: ${g.description || g.goal_description}`, severity: "info" })}
          onAddBarrier={() => handleAddToChart("Barrier")}
          onEditBarrier={(b) => onShowSnackbar({ open: true, message: `Edit barrier: ${b.barrier_type || b.type}`, severity: "info" })}
          onAddCondition={() => handleAddToChart("Problem")}
        />
      ),
      rpmSummary: (
        <RpmSummaryCard
          patientId={selectedPatientId}
          patientSummary={patientSummary}
          apiBase={API}
          onShowSnackbar={onShowSnackbar}
          onAddVitals={() => handleAddToChart("RPM Reading")}
          onAddCondition={() => handleAddToChart("Problem")}
        />
      ),
      bhiSummary: (
        <BhiSummaryCard
          patientId={selectedPatientId}
          patientSummary={patientSummary}
          assessments={assessments}
          apiBase={API}
          onShowSnackbar={onShowSnackbar}
          onStartAssessment={handleStartAssessment}
          onAddBarrier={() => handleAddToChart("Barrier")}
          onEditBarrier={(b) => onShowSnackbar({ open: true, message: `Edit barrier: ${b.barrier_type || b.type}`, severity: "info" })}
          onAddGoal={() => handleAddToChart("Goal")}
          onEditGoal={(g) => onShowSnackbar({ open: true, message: `Edit goal: ${g.description || g.goal_description}`, severity: "info" })}
          onAddCondition={() => handleAddToChart("Problem")}
        />
      ),
    };
    return map[id] ?? null;
  };

  return (
    <Box sx={{ display: "flex", width: "100%" }}>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: "100%",
          transition: theme => theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          ...(encounterDrawerOpen && {
            transition: theme => theme.transitions.create('margin', {
              easing: theme.transitions.easing.easeOut,
              duration: theme.transitions.duration.enteringScreen,
            }),
            marginRight: { xs: 0, sm: 0, md: '380px', lg: '420px', xl: '460px' },
          }),
        }}
        data-workflow-step="patient_chart_view"
        data-clinical-context="patient_management"
        data-patient-condition={patientSummary?.problems?.length > 0 ? patientSummary.problems[0].name : "unknown"}
        data-visit-type="chart_review"
        data-timestamp={Date.now()}
      >
        <PatientOverviewBanner
          patient={patient}
          patientSummary={patientSummary}
          nkdaChecked={nkdaChecked}
          onNkdaChange={(e) => setNkdaChecked(e.target.checked)}
          onAddAllergy={() => handleAddToChart("Allergy")}
          onAddEncounter={() => handleAddToChart("Encounter")}
          onAddAppointment={() => handleAddToChart("Appointment")}
          onAddInsurance={() => handleAddToChart("Insurance")}
          onAddProgramEnrollment={() => handleAddToChart("Program Enrollment")}
          onCreateCarePlan={handleCreateCarePlan}
          onOpenDeviceDialog={() => setDeviceDialogOpen(true)}
        />

        <DeviceOrderDialog
          open={deviceDialogOpen}
          formData={deviceFormData}
          onChange={setDeviceFormData}
          onCancel={() => setDeviceDialogOpen(false)}
          onSubmit={handleDeviceOrderSubmit}
        />

        {/* Expand / Collapse all controls */}
        <Stack direction="row" spacing={1} sx={{ mb: 1, px: 0.5, alignItems: "center", flexWrap: "wrap" }}>
          <Button
            size="small"
            variant="text"
            startIcon={<UnfoldMoreIcon sx={{ fontSize: 14 }} />}
            onClick={expandAll}
            sx={{ fontSize: "0.7rem", py: 0.25, color: "text.secondary" }}
          >
            Expand all
          </Button>
          <Button
            size="small"
            variant="text"
            startIcon={<UnfoldLessIcon sx={{ fontSize: 14 }} />}
            onClick={collapseAll}
            sx={{ fontSize: "0.7rem", py: 0.25, color: "text.secondary" }}
          >
            Collapse all
          </Button>

          {enrolledPrograms.size > 0 && (
            <>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.65rem", letterSpacing: 0.5, fontWeight: 600 }}>
                PROGRAM LENS
              </Typography>
              {["CCM", "RPM", "BHI"].filter((p) => enrolledPrograms.has(p)).map((program) => {
                const active = activeLenses.has(program);
                const color = PROGRAM_COLORS[program];
                return (
                  <Chip
                    key={program}
                    label={program}
                    size="small"
                    onClick={() => toggleLens(program)}
                    variant={active ? "filled" : "outlined"}
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.7rem",
                      height: 24,
                      bgcolor: active ? color.light : "transparent",
                      color: active ? color.main : "text.secondary",
                      borderColor: active ? color.main : "divider",
                      "&:hover": { bgcolor: color.light },
                    }}
                  />
                );
              })}
            </>
          )}
        </Stack>

        {/* Full-width summary cards for active program lenses */}
        {activeLenses.size > 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
            {["CCM", "RPM", "BHI"]
              .filter((p) => activeLenses.has(p))
              .map((p) => {
                const cardId = PROGRAM_SUMMARY_CARD_IDS[p];
                return (
                  <Box key={cardId}>
                    {getCardElement(cardId)}
                  </Box>
                );
              })}
          </Box>
        )}

        <DndContext
          sensors={dndSensors}
          collisionDetection={closestCenter}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <Box
            sx={{
              width: "100%",
              margin: 0,
              display: "grid",
              gap: (theme) => theme.spacing(3),
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(3, minmax(0, 1fr))",
                lg: "repeat(4, minmax(0, 1fr))",
                xl: "repeat(5, minmax(0, 1fr))",
              },
            }}
          >
            {["col1", "col2", "col3", "col4", "col5"].map((col) => (
              <SortableContext
                key={col}
                items={columnOrders[col].filter((id) => !Object.values(PROGRAM_SUMMARY_CARD_IDS).includes(id))}
                strategy={verticalListSortingStrategy}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {columnOrders[col].filter((cardId) => !Object.values(PROGRAM_SUMMARY_CARD_IDS).includes(cardId)).map((cardId) => (
                    <ChartCardWrapper
                      key={cardId}
                      id={cardId}
                      label={CARD_LABELS[cardId] || cardId}
                      isHighlighted={agendaCardIds.has(cardId)}
                      highlightColor={getLensColor(cardId)}
                      collapsed={collapsed[cardId] || false}
                      onToggle={() => toggleCollapsed(cardId)}
                    >
                      {getCardElement(cardId)}
                    </ChartCardWrapper>
                  ))}
                </Box>
              </SortableContext>
            ))}
          </Box>
        </DndContext>

        {/* Dialogs */}
        <GenericFormDialog
          open={formDialogOpen}
          title={`${formData._editingId ? 'Edit' : 'Add'} ${formType}`}
          submitLabel={`${formData._editingId ? 'Update' : 'Save'} ${formType}`}
          fields={getFormFields(formType, formData)}
          formData={formData}
          onChange={(next) => setFormData(
            formType === "RPM Reading" && next.reading_type !== formData.reading_type
              ? changeReadingType(next, next.reading_type)
              : next
          )}
          onCancel={() => setFormDialogOpen(false)}
          onSubmit={handleSubmitForm}
          formType={formType}
        />

        <CarePlanDialog
          open={carePlanDialogOpen}
          patients={patients}
          selectedPatientId={selectedPatientId}
          selectedPatientName={selectedPatientName}
          formData={carePlanFormData}
          onChange={setCarePlanFormData}
          onCancel={() => setCarePlanDialogOpen(false)}
          onSubmit={handleSaveCarePlan}
        />

        <LookupDialog
          open={lookupState.icd.open}
          title="ICD-10 Code Lookup"
          searchLabel="Search ICD-10 Codes"
          placeholder="Enter diagnosis name or code..."
          query={lookupState.icd.query}
          onQueryChange={(value) => handleLookupSearch("icd", value)}
          results={lookupState.icd.results}
          onSelect={(result) => handleLookupSelect("icd", result)}
          minQueryLength={2}
          searchType="icd10"
          searchContext="problem_diagnosis"
          onClose={() =>
            setLookupState((prev) => ({ ...prev, icd: { ...prev.icd, open: false } }))
          }
        />

        <LookupDialog
          open={lookupState.cpt.open}
          title="CPT Code Lookup"
          searchLabel="Search CPT Codes"
          placeholder="Enter procedure name or code..."
          query={lookupState.cpt.query}
          onQueryChange={(value) => handleLookupSearch("cpt", value)}
          results={lookupState.cpt.results}
          onSelect={(result) => handleLookupSelect("cpt", result)}
          minQueryLength={2}
          searchType="cpt"
          searchContext="procedure"
          onClose={() =>
            setLookupState((prev) => ({ ...prev, cpt: { ...prev.cpt, open: false } }))
          }
        />

        <LookupDialog
          open={lookupState.lab.open}
          title="Lab Test Lookup (LOINC)"
          searchLabel="Search LOINC Codes"
          placeholder="Enter lab test name or code..."
          query={lookupState.lab.query}
          onQueryChange={(value) => handleLookupSearch("lab", value)}
          results={lookupState.lab.results}
          onSelect={(result) => handleLookupSelect("lab", result)}
          minQueryLength={2}
          searchType="loinc"
          searchContext="lab_order"
          onClose={() =>
            setLookupState((prev) => ({ ...prev, lab: { ...prev.lab, open: false } }))
          }
        />

        <LookupDialog
          open={lookupState.med.open}
          title="Medication Lookup"
          searchLabel="Search Medications"
          placeholder="Enter medication name or code..."
          query={lookupState.med.query}
          onQueryChange={(value) => handleLookupSearch("med", value)}
          results={lookupState.med.results}
          onSelect={(result) => handleLookupSelect("med", result)}
          minQueryLength={2}
          searchType="medication"
          searchContext="medication_order"
          onClose={() =>
            setLookupState((prev) => ({ ...prev, med: { ...prev.med, open: false } }))
          }
        />

        <PreventiveCareCompletionDialog
          open={preventiveDialogOpen}
          item={preventiveItem}
          formData={preventiveFormData}
          onChange={setPreventiveFormData}
          onCancel={() => setPreventiveDialogOpen(false)}
          onSubmit={handleSavePreventiveCareCompletion}
        />

        <AssessmentDialog
          open={assessmentDialogOpen}
          template={currentAssessmentType ? assessmentTemplates[currentAssessmentType] : null}
          responses={assessmentResponses}
          onChange={handleAssessmentResponseChange}
          onCancel={() => setAssessmentDialogOpen(false)}
          onSubmit={handleSubmitAssessment}
        />

        <GenericFormDialog
          open={demographicsDialogOpen}
          title="Edit Demographics"
          fields={[
            { name: "phone", label: "Phone Number", type: "text", category: "contact" },
            { name: "email", label: "Email", type: "email", category: "contact" },
            { name: "address", label: "Address", type: "text", category: "contact" },
            { name: "city", label: "City", type: "text", category: "contact" },
            { name: "state", label: "State", type: "text", category: "contact" },
            { name: "zip", label: "ZIP Code", type: "text", category: "contact" },
            { name: "dob", label: "Date of Birth", type: "date", category: "demographics" },
            {
              name: "sex", label: "Sex", type: "select", category: "demographics", options: [
                { value: "M", label: "Male" },
                { value: "F", label: "Female" },
                { value: "O", label: "Other" }
              ]
            },
          ]}
          formData={demographicsFormData}
          onChange={setDemographicsFormData}
          onCancel={() => {
            setDemographicsDialogOpen(false);
            setDemographicsFormData({});
          }}
          onSubmit={handleSaveDemographics}
          submitLabel="Save Demographics"
          formType="demographics"
        />

        {/* Open drawer button: labelled, with the call time so far */}
        {patient && !encounterDrawerOpen && (
          <Button
            onClick={handleOpenEncounterDrawer}
            startIcon={<ChevronLeftIcon />}
            sx={{
              position: "fixed",
              top: "25%",
              right: 0,
              zIndex: 1300,
              pl: 1.25,
              pr: 1.75,
              py: 0.75,
              borderRadius: "999px 0 0 999px",
              bgcolor: "background.paper",
              color: "primary.main",
              fontWeight: 700,
              letterSpacing: "0.06em",
              "&:hover": {
                bgcolor: "action.hover",
              },
              boxShadow: 3,
            }}
            aria-label="Open encounter panel"
            data-action="open-encounter-drawer"
            data-session-id={sessionId}
            data-timestamp={Date.now()}
          >
            Encounter note
            <Box component="span" sx={{ ml: 1, fontWeight: 500, opacity: 0.7, fontVariantNumeric: "tabular-nums" }}>
              {formatCallTime(callElapsed)}
            </Box>
          </Button>
        )}
      </Box>

      <EncounterDrawer
        open={encounterDrawerOpen}
        onClose={handleEncounterDrawerClose}
        patient={patient}
        session={activeEncounterSession}
        apiBase={API}
        loadingSession={encounterSessionLoading}
        onSessionChange={handleEncounterSessionChange}
        onShowSnackbar={onShowSnackbar}
        onEncounterCompleted={handleEncounterComplete}
        agendaData={preCallData ? { total_items: preCallData.total_items || 0, addressed: (preCallData.agenda_items || []).filter(i => i.addressed).length } : null}
        chartActivityLog={chartActivityLog}
        callElapsedSeconds={callElapsed}
      />
    </Box>
  );
}

PatientChartContainer.propTypes = {
  patient: PropTypes.object,
  patients: PropTypes.array.isRequired,
  apiBase: PropTypes.string,
  onShowSnackbar: PropTypes.func.isRequired,
};

PatientChartContainer.defaultProps = {
  patient: null,
  apiBase: "https://spectra-api.loafstack.dev",
};

export default PatientChartContainer;

